import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { MarineInsuranceModel } from '@data-layer/models/marine-insurance';
import { UntypedFormGroup } from '@angular/forms';
import { CUSTOM_MASK } from '@helpers/custom-validator';
import { IsfModel } from '@data-layer/models/isf';
import { EntrySummaryModel } from '@data-layer/models/entry-summaries';
import { LCLShipmentModel } from '@data-layer/models/lcl';
import { enumInsuranceShipmentType } from '@data-layer/enums/marine-insurance';
import { IDefaultSelect } from '@interfaces/default-select';
import { TranslateService } from '@ngx-translate/core';
import { enumTransportationMode } from '@data-layer/enums/entry-summaries';
import { enumIsfShipmentCode } from '@data-layer/enums/isf';

@Component({
  selector: 'app-marine-insurance-quote-shipment-details-card',
  templateUrl: './marine-insurance-quote-shipment-details-card.component.html',
  styleUrls: ['./marine-insurance-quote-shipment-details-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarineInsuranceQuoteShipmentDetailsCardComponent implements OnInit {
  @Input() public marineInsurance: MarineInsuranceModel;
  @Input() public isf: IsfModel;
  @Input() public entrySummary: EntrySummaryModel;
  @Input() public lclShipment: LCLShipmentModel;
  @Input() public formGroup: UntypedFormGroup;

  public CUSTOM_MASK: typeof CUSTOM_MASK = CUSTOM_MASK;
  public defaultErrors: { [name: string]: string } = {
    required: 'FORM_ERROR.REQUIRED',
    dateInvalid: 'MARINE_INSURANCE_QUOTE_VALIDATION_OCEAN',
  };
  public insuranceShipmentTypes: IDefaultSelect[];

  constructor(private translateService: TranslateService, private cdr: ChangeDetectorRef) {}

  public ngOnInit(): void {
    this.insuranceShipmentTypes = Object.keys(enumInsuranceShipmentType)
      .filter((item: string) => Number(item) > -1)
      .map((item: any) => ({
        id: Number(item),
        label: this.translateService.instant('MARINE_INSURANCE_SHIPMENT_TYPE.' + enumInsuranceShipmentType[item]),
        value: Number(item),
      }));
  }

  public updateInputsValidation(): void {
    this.cdr.markForCheck();
  }

  public get billNumber(): string {
    if (!!this.isf) {
      return this.isf?.shipment?.code === enumIsfShipmentCode.HOUSE
        ? `${this.isf?.shipment?.houseBill?.carrier || ''}${this.isf?.shipment?.houseBill?.number || ''}`
        : `${this.isf?.shipment?.masterBill?.carrier || ''}${this.isf?.shipment?.masterBill?.number || ''}`;
    }

    if (!!this.entrySummary) {
      return this.entrySummary?.shipments?.[0]?.code === enumIsfShipmentCode.HOUSE
        ? `${this.entrySummary?.shipments?.[0]?.houseBill?.carrier || ''}${this.entrySummary?.shipments?.[0]?.houseBill?.number || ''}`
        : `${this.entrySummary?.shipments?.[0]?.masterBill?.carrier || ''}${this.entrySummary?.shipments?.[0]?.masterBill?.number || ''}`;
    }

    if (!!this.lclShipment) {
      return `${this.lclShipment?.houseBill?.carrier || ''}${this.lclShipment?.houseBill?.number || ''}`;
    }
  }

  public get isShowForm(): boolean {
    return !this.lclShipment;
  }

  public get vesselNameLabel(): string {
    if (!!this.isf || !!this.lclShipment) {
      return 'LABELS.VESSEL_NAME';
    } else {
      return this.entrySummary?.transportationMode === enumTransportationMode.OCEAN ? 'LABELS.VESSEL_NAME' : 'AIRLINE_PREFIX';
    }
  }

  public get voyageNumberLabel(): string {
    if (!!this.isf || !!this.lclShipment) {
      return 'LABELS.VOYAGE_NUMBER';
    } else {
      return this.entrySummary?.transportationMode === enumTransportationMode.OCEAN ? 'LABELS.VOYAGE_NUMBER' : 'FORM_LABEL.FLIGHT_NUMBER';
    }
  }

  public get isAirwayEntry(): boolean {
    return this.entrySummary?.transportationMode === enumTransportationMode.AIR;
  }

  public get typeOfShipmentValue(): string {
    return `MARINE_INSURANCE_SHIPMENT_TYPE.${enumInsuranceShipmentType[this.marineInsurance?.insuredValue?.typeOfShipment]}`;
  }
}
